/**
 * Browser-safe battle state hash (design D4/D5): a canonical plain-data snapshot of everything that drives a running
 * battle, hashed with the session hasher (src/session/hash.ts, canonical JSON + 64-bit hash). The client uses it for
 * turn checkpoints and the final hash of a battle, the server's battle simulator for the same values, so both must
 * run exactly this code (no node imports here).
 *
 * The snapshot covers the simulation, not the presentation:
 *  - proc: battle tick, move timer, turn counters, current team/worm, wind/gravity, the move logic's action and
 *    timeout, control flags (game control, map-click mode, worm choice, girder shape, strike direction), the AI's
 *    scalar state, the achievement counters;
 *  - the pseudo-random cursor FWP.cur_random (the only randomness of the simulation, FWP.pseudo_random);
 *  - teams (state, AI level, last worm, turns, the arsenal left: weapon counts per rank) and the worm list;
 *  - every live proc.OBJ slot except visual effects ("E": explosions, smoke, damage numbers placed with true_random)
 *    and the AI's virtual projectile ("V"): slot, model, type, position, the plain data of vars / states / weapon /
 *    weapon_flags / affected_params, skills, perks and the other per-object scalars;
 *  - a digest of the terrain pixels.
 * Display-object names ("objN", counted across battles by FWP.max_object), the camera and the interface are not part
 * of it, so a re-simulation in another session (or another process) hashes equal.
 */
import { type StateHasher, defaultStateHasher } from '../../session/hash';

/** Version of the snapshot layout (part of the snapshot: a changed layout never compares equal). */
export const BATTLE_SNAPSHOT_VERSION = 1;

/** FNV-1a over the premultiplied ARGB pixels of the terrain bitmap (0 without a map). */
export function terrainDigest(bitmap: any): number {
  const data: Uint32Array | undefined = bitmap?.$data;
  let h = 0x811c9dc5;
  if (data) for (let i = 0; i < data.length; i++) h = Math.imul(h ^ data[i], 0x01000193);
  return h >>> 0;
}

/** Plain data of a value: numbers, strings, booleans and nested plain objects / arrays (depth-limited); no display objects. */
function plain(v: any, depth = 0): any {
  if (v === null || v === undefined) return v === null ? null : undefined;
  const t = typeof v;
  if (t === 'number' || t === 'string' || t === 'boolean') return v;
  if (t !== 'object' || depth >= 3) return undefined;
  if (Array.isArray(v)) return v.map((x) => plain(x, depth + 1) ?? null);
  const proto = Object.getPrototypeOf(v);
  if (proto !== Object.prototype && proto !== null) return undefined;
  const out: Record<string, any> = {};
  for (const k of Object.keys(v)) {
    const x = plain(v[k], depth + 1);
    if (x !== undefined) out[k] = x;
  }
  return out;
}

const OBJ_SCALARS = [
  'model',
  'ptype',
  'wid',
  'wteam',
  'wname',
  'ai',
  'death',
  'sick',
  'sick_next',
  'dmg_mult',
  'ap_shot',
  'parent_wid',
  'seek_worm',
  'worm_impact',
  'weapon_id',
  'weapon_range',
  'weapon_name',
  'affected',
  'buttonid',
  'spressed',
  'kamikaze_effect',
  'cur_shot_fail',
] as const;

const OBJ_RECORDS = ['vars', 'states', 'weapon', 'weapon_flags', 'affected_params', 'skills', 'perks'] as const;

const PROC_SCALARS = [
  'battle_tick',
  'timer',
  'game_turn',
  'my_turn',
  'cur_team',
  'cur_worm',
  'W',
  'G',
  'WKEYDELAY',
  'TURNTIME',
  'proc_timeout',
  'game_control',
  'mousecontrol',
  'choose_worm',
  'choose_worm_id',
  'choose_worm_timeout',
  'choose_delay',
  'strike_dir',
  'girder_id',
  'girder_key_delay',
  'wait_for_ready',
  'worm_pid_to_die',
  'max_team_hp',
  'CRATES_ON_MAP',
  'SURV_KILLS',
  'CUR_STEALTH',
  'STEALTH_DURATION',
  'fire_on_map',
  'laser_activated',
  'napalm_used',
  'mission16_turn',
  'ach_last_wptype',
  'ach_paralyzed',
  'ach_annihilated',
  'ach_injure',
  'ach_kill',
  'ai_buttonid',
  'ai_spressed',
  'ai_pos',
  'ai_target',
  'ai_weapon',
  'ai_sleep',
  'ai_move_dir',
  'ai_move_steps',
  'ai_move_state',
  'ai_max_targets',
  'ai_dest_iters',
  'ai_dest_time',
  'ai_trav_time',
] as const;

/**
 * Canonical snapshot of the running battle of `fwp` (FWP instance), or null when no battle runs. Plain JSON data;
 * `battleHash` hashes it.
 */
export function battleSnapshot(fwp: any): Record<string, unknown> | null {
  const p = fwp?._proc;
  if (!p) return null;
  const proc: Record<string, unknown> = {};
  for (const k of PROC_SCALARS) proc[k] = plain(p[k]) ?? null;
  proc.action = plain(p.proc_action);
  proc.ach_killed = plain(p.ach_killed);
  proc.ai_fire_state = plain(p.ai_fire_state);
  proc.ai_proj_prop = plain(p.ai_proj_prop);
  proc.worms_init_health = plain(p.worms_init_health);
  const teams: unknown[] = [];
  for (let i = 1; i <= p.teams[0]; i++) {
    const t = p.teams[i];
    teams.push({ state: t.state, cpu_level: t.cpu_level, last_worm: t.last_worm, turns: t.turns, worms: t.worms, weapons: plain(t.weapons) });
  }
  const worms: unknown[] = [];
  for (let i = 1; i <= p.worms[0]; i++) {
    const w = p.worms[i];
    const o = fwp._obj[w.fid];
    worms.push([w.state, o ? o.pid : 0]);
  }
  const objects: unknown[] = [];
  for (let i = 1; i <= p.OBJ[0]; i++) {
    const s = p.OBJ[i];
    if (!s || s.free) continue;
    const o = fwp._obj[s.fid];
    if (!o || o.model === 'E' || o.model === 'V') continue;
    const rec: Record<string, unknown> = { slot: i, ready: !!s.ready, x: o.x, y: o.y };
    for (const k of OBJ_SCALARS) rec[k] = plain(o[k]) ?? null;
    for (const k of OBJ_RECORDS) rec[k] = plain(o[k]) ?? null;
    objects.push(rec);
  }
  const bmp = fwp._world?.map_bitmap;
  return {
    v: BATTLE_SNAPSHOT_VERSION,
    mode: String(p._play_type),
    stage: Number(p._play_id),
    random: fwp.cur_random,
    proc,
    teams,
    worms,
    objects,
    terrain: bmp ? [bmp.$width, bmp.$height, terrainDigest(bmp)] : null,
  };
}

/** Hash of the running battle of `fwp` ('no-battle' when none runs). */
export function battleHash(fwp: any, hasher: StateHasher = defaultStateHasher): string {
  const s = battleSnapshot(fwp);
  return s ? hasher.hash(s) : 'no-battle';
}
